// Detail pane → WhatsApp Flow. Replaces the four conditional Flow items that
// used to inflate the per-workflow menu.

import { Badge, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { MdCloudUpload, MdCode, MdSync } from 'react-icons/md';
import { Chatbot } from '@/lib/sdk/chatbot/types';
import { getFlowState } from '../flowState';

interface FlowTabProps {
  chatbot: Chatbot;
  isPreviewing: boolean;
  isSyncing: boolean;
  isPublishing: boolean;
  isDeleting: boolean;
  onPreview: () => void;
  onSync: () => void;
  onPublish: () => void;
  onDelete: () => void;
}

export function FlowTab({
  chatbot,
  isPreviewing,
  isSyncing,
  isPublishing,
  isDeleting,
  onPreview,
  onSync,
  onPublish,
  onDelete,
}: FlowTabProps) {
  const flowState = getFlowState(chatbot);
  // Only one Flow admin action can be in flight at a time server-side anyway (they all
  // mutate the same Meta Flow resource) — disabling the rest while one runs keeps that
  // visible instead of letting a second click queue up behind it silently.
  const isMutating = isPreviewing || isSyncing || isPublishing || isDeleting;

  if (!flowState) {
    return (
      <Text data-testid="wfl-flow-disabled" fontSize="sm" color="text-secondary">
        WhatsApp Flows are disabled for this workflow. Enable them under Settings → WhatsApp Flows.
      </Text>
    );
  }

  return (
    <VStack align="stretch" spacing={5} maxW="640px" data-testid="wfl-flow-panel">
      <HStack
        justify="space-between"
        borderWidth="1px"
        borderColor="border-default"
        borderRadius="md"
        px={4}
        py={3}
      >
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="sm" fontWeight="medium" color="text-primary">Flow status</Text>
          <Text fontSize="xs" color="text-secondary">
            {chatbot.flow_id
              ? `Flow ID ${chatbot.flow_id}`
              : 'No Flow has been synced to Meta yet'}
          </Text>
        </VStack>
        <Badge colorScheme={flowState.colorScheme}>{flowState.label}</Badge>
      </HStack>

      <HStack spacing={2} flexWrap="wrap" rowGap={2}>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<MdCode />}
          onClick={onPreview}
          isLoading={isPreviewing}
          loadingText="Generating"
          isDisabled={isMutating && !isPreviewing}
        >
          Preview JSON
        </Button>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<MdSync />}
          onClick={onSync}
          isLoading={isSyncing}
          loadingText="Syncing"
          isDisabled={isMutating && !isSyncing}
        >
          Sync flow
        </Button>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<MdCloudUpload />}
          onClick={onPublish}
          isLoading={isPublishing}
          loadingText="Publishing"
          isDisabled={!chatbot.flow_id || chatbot.flow_published || (isMutating && !isPublishing)}
        >
          Publish flow
        </Button>
        <Button
          size="sm"
          variant="outline"
          colorScheme="red"
          onClick={onDelete}
          isLoading={isDeleting}
          loadingText={chatbot.flow_published ? 'Deprecating' : 'Deleting'}
          isDisabled={!chatbot.flow_id || (isMutating && !isDeleting)}
        >
          {chatbot.flow_published ? 'Deprecate flow' : 'Delete flow'}
        </Button>
      </HStack>
    </VStack>
  );
}
